// BUILD_MARKER_JAN6_2026
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import { toast } from "sonner";
import { Bell, Calendar, LogOut, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const alertsRes = await axiosInstance.get("/alerts");
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post("/auth/logout");
      setUser(null);
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  const handleCancelAlert = async (alertId) => {
    try {
      await axiosInstance.delete(`/alerts/${alertId}`);
      toast.success("Alert cancelled successfully");
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to cancel alert");
    }
  };

  const activeAlerts = alerts.filter(a => a.active);
  const cancelledAlerts = alerts.filter(a => !a.active);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-beige-100">
        <div className="text-xl font-medium">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-stone-50 to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate("/")}>
            <img 
              src="https://customer-assets.emergentagent.com/job_sitehunter-1/artifacts/rlsp35z7_CampPing%20UI%20Icon.png" 
              alt="CampSight" 
              className="w-10 h-10" 
            />
            <h1 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Lobster Two, cursive', fontWeight: 700}}>
              CampSight
            </h1>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <Avatar>
                <AvatarImage src={user?.picture} />
                <AvatarFallback><User className="w-4 h-4" /></AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <Button 
              onClick={handleLogout} 
              variant="outline"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Free Service Banner - Always visible */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 mb-8 text-white shadow-xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <span className="text-3xl">🎉</span>
                <h2 className="text-2xl font-bold">Don&apos;t Worry, It&apos;s Free!</h2>
              </div>
              <p className="text-green-100">
                CampSight is completely free during early access.
              </p>
            </div>
            <Button
              onClick={() => navigate("/")}
              className="bg-white text-green-700 hover:bg-green-50 font-semibold px-6 py-3 whitespace-nowrap"
            >
              Start Exploring Campsites →
            </Button>
          </div>
        </div>

        {/* Alerts Usage Banner */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 mb-8 text-white shadow-xl">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-1">Your Active Alerts</h2>
              <p className="text-orange-100">You can have up to 3 active alerts at a time</p>
            </div>
            <div className="mt-4 md:mt-0 text-center">
              <div className="text-5xl font-bold">{activeAlerts.length} / 3</div>
              <p className="text-orange-100 text-sm">alerts used</p>
            </div>
          </div>
        </div>

        {/* Alerts Section */}
        <div className="bg-white rounded-2xl shadow-md">
          <div className="border-b px-6 py-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Bell className="w-5 h-5" />
              My Alerts ({activeAlerts.length})
            </h3>
          </div>
          
          <div className="p-6">
            {activeAlerts.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Active Alerts</h3>
                <p className="text-gray-500 mb-4">
                  Start creating alerts to get notified about campsite availability!
                </p>
                <Button
                  onClick={() => navigate("/")}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Browse Campsites
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {activeAlerts.map((alert) => (
                  <div key={alert.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-gray-800">{alert.park_name}</h4>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {alert.start_date} to {alert.end_date}
                          </span>
                          <span>Site #{alert.site_number}</span>
                          <span className="capitalize">{alert.site_type}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Created: {new Date(alert.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleCancelAlert(alert.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cancelled Alerts */}
            {cancelledAlerts.length > 0 && (
              <div className="mt-8 pt-6 border-t">
                <h4 className="text-sm font-medium text-gray-500 mb-4">Cancelled Alerts</h4>
                <div className="space-y-2">
                  {cancelledAlerts.map((alert) => (
                    <div key={alert.id} className="border rounded-lg p-3 bg-gray-50 opacity-60">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-gray-700">{alert.park_name}</h4>
                          <p className="text-sm text-gray-500">
                            {alert.start_date} to {alert.end_date} • Site #{alert.site_number}
                          </p>
                        </div>
                        <Badge variant="secondary">Cancelled</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        {user?.alert_credits > 0 && (
          <div className="mt-8 text-center">
            <Button
              onClick={() => navigate("/")}
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8"
            >
              <Bell className="w-5 h-5 mr-2" />
              Create New Alert
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
